import React, { type ReactNode } from "react";
import type {
  SchemaFieldDefinition,
  SchemaFormDefinition,
  SchemaFormValues,
} from "../types";
import { isDataField } from "../helpers";
import type { SchemaFieldDictionary } from "../helpers";

interface SchemaFormLayoutProps<FieldKey extends string> {
  schema: SchemaFormDefinition<FieldKey>;
  values: SchemaFormValues<FieldKey>;
  fieldStates: Record<FieldKey, { visible: boolean; disabled: boolean }>;
  sectionVisibility: Record<string, boolean>;
  fieldDictionary: SchemaFieldDictionary<FieldKey>;
  translate: (key?: string, arg2?: string | Record<string, unknown>, fallback?: string) => string;
  renderFieldNode: (
    field: SchemaFieldDefinition<FieldKey>,
    path: string,
    options?: { ignoreSuppression?: boolean }
  ) => ReactNode;
  suppressedFieldKeys: Set<FieldKey>;
}

export const SchemaFormLayout = <FieldKey extends string>({
  schema,
  fieldStates,
  sectionVisibility,
  fieldDictionary,
  translate,
  renderFieldNode,
  suppressedFieldKeys,
}: SchemaFormLayoutProps<FieldKey>) => {
  const renderFieldGrid = (
    fields: Array<SchemaFieldDefinition<FieldKey>>,
    basePath: string,
    columns?: number
  ) => {
    const rawColumns = columns ?? schema.layout?.columns ?? 1;
    const columnCount = Math.max(1, Math.min(rawColumns, 10));
    // 使用内联样式始终应用多列布局，不依赖响应式断点
    // 这确保在 PromptDock 等紧凑场景下参数也能横向排列
    return (
      <div
        className="grid gap-2"
        style={{ gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))` }}
      >
        {fields.map((field, index) => {
          const path = `${basePath}.${index}`;
          const content = renderFieldNode(field, path);
          if (!content) return null;
          const desiredSpan = field.layout?.colSpan ?? 1;
          const colSpan = Math.max(1, Math.min(desiredSpan, columnCount));
          return (
            <div
              key={path}
              className={field.className as string | undefined}
              data-field-key={(field as any)?.key as string | undefined}
              style={{
                gridColumn: `span ${colSpan} / span ${colSpan}`,
                position: field.layout?.sticky ? ("sticky" as const) : undefined,
                top: field.layout?.sticky ? "64px" : undefined,
                ...sanitizeSxToStyle(field.sx),
              }}
            >
              {content}
            </div>
          );
        })}
      </div>
    );
  };

  const layoutSections = schema.layout?.sections ?? [];
  const usedFieldKeys = new Set<FieldKey>();
  const sectionBlocks = layoutSections.map((section) => {
    const visible = sectionVisibility[section.id] ?? true;
    if (!visible) return null;
    const sectionFields =
      section.fields?.map((key) => {
        const field = fieldDictionary.get(key);
        if (field) {
          usedFieldKeys.add(key);
        }
        return field;
      }) ?? [];
    const filteredFields = sectionFields.filter(Boolean) as Array<
      SchemaFieldDefinition<FieldKey>
    >;
    if (filteredFields.length === 0) {
      return null;
    }
    return (
      <div key={section.id} className="flex flex-col gap-3">
        {section.titleKey ? (
          <div className="text-base font-semibold">{translate(section.titleKey)}</div>
        ) : null}
        {section.descriptionKey ? (
          <div className="text-sm text-muted-foreground">{translate(section.descriptionKey)}</div>
        ) : null}
        {renderFieldGrid(filteredFields, `section-${section.id}`, section.columns)}
      </div>
    );
  });

  const remainingFields = schema.fields.filter(
    (field): field is SchemaFieldDefinition<FieldKey> =>
      !isDataField(field) || (field.key ? !usedFieldKeys.has(field.key) : true)
  );

  return (
    <>
      {sectionBlocks}
      {renderFieldGrid(remainingFields, "root")}
    </>
  );
};

const sanitizeSxToStyle = (sx: unknown): React.CSSProperties => {
  if (!sx || typeof sx !== "object") return {};
  const style: React.CSSProperties = {};
  for (const [rawKey, val] of Object.entries(sx as Record<string, unknown>)) {
    const key = String(rawKey);
    if (
      key.startsWith("--") ||
      (!key.includes("&") &&
        !key.includes(" ") &&
        !key.includes(",") &&
        !key.includes(">") &&
        !key.includes(":") &&
        !key.includes("."))
    ) {
      (style as any)[key] = val as never;
    }
  }
  return style;
};
